import { useEffect, useRef, useState } from 'react';
import { v2, type NotificationApi } from '@/api/v2Client';

// Phase 5.X-notifications-system — SSE consumer hook for the
// notifications stream.
//
// Lifecycle:
//   - On mount: opens an async-generator consumer of
//     `v2.notifications.stream(signal)`. The generator yields one
//     `NotificationApi` per incoming `event: notification` frame.
//   - On each yield: invokes `onNotification(notification)` so the
//     caller can update its local state (bell badge, dropdown list,
//     full page).
//   - On error / disconnect: schedules a reconnect with exponential
//     backoff (1s → 2s → 5s → 10s → 30s ceiling). Resets the backoff
//     to 0 after a stretch of successful streaming (the timer resets
//     once the first notification arrives, signalling a healthy
//     stream).
//   - On unmount: aborts the in-flight stream via `AbortController`,
//     cancels any pending reconnect timer.
//
// `enabled` lets callers gate the hook (e.g., wait until auth has
// resolved). When false, no stream is opened.

const BACKOFF_SCHEDULE_MS: ReadonlyArray<number> = [1000, 2000, 5000, 10000, 30000];

export interface UseNotificationsStreamOptions {
  /** Whether the hook should attempt to connect. Default: true. */
  readonly enabled?: boolean;
  /** Called with each incoming notification. */
  readonly onNotification: (n: NotificationApi) => void;
}

export interface UseNotificationsStreamResult {
  /** True when the stream is currently connected (post-first-yield). */
  readonly connected: boolean;
  /** Last connection error message, if any. Cleared on successful reconnect. */
  readonly error: string | null;
}

export function useNotificationsStream(
  options: UseNotificationsStreamOptions,
): UseNotificationsStreamResult {
  const { enabled = true, onNotification } = options;
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use refs to keep the latest callbacks visible to the long-lived
  // async loop without re-binding it every render.
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let backoffIdx = 0;
    let reconnectTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
    let controller: globalThis.AbortController | null = null;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      controller = new globalThis.AbortController();
      try {
        for await (const notification of v2.notifications.stream(controller.signal)) {
          if (cancelled) return;
          setConnected(true);
          setError(null);
          backoffIdx = 0; // healthy stream — reset backoff
          onNotificationRef.current(notification);
        }
        // Stream ended cleanly (server closed connection). Reconnect.
        if (!cancelled) scheduleReconnect();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.name === 'AbortError') {
          // Caller-initiated abort (unmount) — don't reconnect.
          return;
        }
        setConnected(false);
        setError(err instanceof Error ? err.message : 'Stream error');
        scheduleReconnect();
      }
    };

    const scheduleReconnect = (): void => {
      if (cancelled) return;
      const delay = BACKOFF_SCHEDULE_MS[Math.min(backoffIdx, BACKOFF_SCHEDULE_MS.length - 1)]!;
      backoffIdx += 1;
      reconnectTimer = globalThis.setTimeout(() => {
        reconnectTimer = null;
        void tick();
      }, delay);
    };

    void tick();

    return () => {
      cancelled = true;
      setConnected(false);
      if (reconnectTimer) {
        globalThis.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (controller) {
        controller.abort();
        controller = null;
      }
    };
  }, [enabled]);

  return { connected, error };
}
